import api from './axiosApi'

// Types from OpenAPI spec
export interface UserSummary {
  id: string
  email: string | null
  display_name: string | null
}

export interface PlanShareRead {
  id: string
  user: UserSummary | null
  permission: 'EDIT'
  /** Owner/admin-granted right to freeze and unfreeze the plan */
  can_freeze: boolean
  shared_by: UserSummary | null
  shared_at: string
}

export interface PlanSharesResponse {
  owner: UserSummary | null
  items: PlanShareRead[]
}

export interface ShareCreateBody {
  user_id: string
  permission?: 'EDIT'
  /** Only the plan owner or an admin may set this */
  can_freeze?: boolean
}

export interface ShareUpdateBody {
  permission?: 'EDIT'
  can_freeze?: boolean
}

// User search types (from /admin/users)
export interface UserRead {
  id: string
  email: string | null
  display_name: string | null
  role: string
}

interface UserSearchParams {
  search: string
  skip?: number
  limit?: number
}

interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

interface PaginatedUsersResponse {
  data: UserRead[]
  pagination: PaginationMeta
}

export interface UserSearchResponse {
  users: UserRead[]
  totalCount: number
  hasMore: boolean
  nextOffset: number | null
}

/**
 * Search users via /admin/users endpoint
 */
export async function searchUsers(
  params: UserSearchParams
): Promise<UserSearchResponse> {
  const { search, skip = 0, limit = 20 } = params

  const response = await api.get<PaginatedUsersResponse>('/admin/users', {
    params: {
      search,
      skip,
      limit,
    },
  })

  const { pagination } = response.data
  const nextOffset = pagination.has_more
    ? pagination.skip + pagination.limit
    : null

  return {
    users: response.data.data,
    totalCount: pagination.total,
    hasMore: pagination.has_more,
    nextOffset,
  }
}

/**
 * Get plan shares via GET /plans/{plan_id}/shares
 */
export async function getPlanShares(
  planId: string
): Promise<PlanSharesResponse> {
  const response = await api.get<PlanSharesResponse>(`/plans/${planId}/shares`)
  return response.data
}

/**
 * Create a plan share via POST /plans/{plan_id}/shares
 */
export async function createPlanShare(
  planId: string,
  data: ShareCreateBody
): Promise<PlanShareRead> {
  const response = await api.post<PlanShareRead>(
    `/plans/${planId}/shares`,
    data
  )
  return response.data
}

/**
 * Update a share's freeze grant via PATCH /plans/{plan_id}/shares/{share_id} (owner only)
 */
export async function updatePlanShare(
  planId: string,
  shareId: string,
  data: ShareUpdateBody
): Promise<PlanShareRead> {
  const response = await api.patch<PlanShareRead>(
    `/plans/${planId}/shares/${shareId}`,
    data
  )
  return response.data
}

/**
 * Remove a user's access via DELETE /plans/{plan_id}/shares/{share_id} (owner only)
 */
export async function deletePlanShare(
  planId: string,
  shareId: string
): Promise<void> {
  await api.delete(`/plans/${planId}/shares/${shareId}`)
}

// Ownership transfer types
export interface OwnershipTransferRequest {
  new_owner_id: string
  keep_as_editor?: boolean
}

export interface OwnershipTransferResponse {
  id: string
  from_owner: UserSummary
  to_owner: UserSummary
  transferred_at: string
  kept_as_editor: boolean
}

/**
 * Transfer ownership of a plan via POST /plans/{plan_id}/ownership-transfers
 */
export async function transferOwnership(
  planId: string,
  newOwnerId: string,
  keepAsEditor = true
): Promise<OwnershipTransferResponse> {
  const response = await api.post<OwnershipTransferResponse>(
    `/plans/${planId}/ownership-transfers`,
    {
      new_owner_id: newOwnerId,
      keep_as_editor: keepAsEditor,
    }
  )
  return response.data
}
